import React, { useState, useEffect, useCallback } from 'react';
import { useSupabaseClient, useUser } from '@supabase/auth-helpers-react';
import { useTranslation } from 'react-i18next';
import { Database } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge"; // Pour afficher le statut
import { formatCurrency } from '@/utils/currencyUtils'; // Assumons que cet utilitaire existe
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, MessageCircle } from 'lucide-react'; // Ajouter MessageCircle pour le lien
import { Link } from 'react-router-dom'; // Importer Link

// Définir un type plus spécifique pour les réservations avec les données client jointes
type BookingWithClient = Database['public']['Tables']['bookings']['Row'] & {
    profiles: {
        display_name: string | null;
        avatar_url: string | null;
    } | null; // La jointure peut retourner null si le profil client n'existe pas
    conversationId?: string | null; // Ajouter l'ID de conversation optionnel
};

const EscortBookingsHistory: React.FC = () => {
    const supabase = useSupabaseClient<Database>();
    const user = useUser();
    const { t, i18n } = useTranslation(['escort-bookings-history', 'common']);
    const [bookings, setBookings] = useState<BookingWithClient[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { toast } = useToast();

    const loadBookingHistory = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        setError(null);

        try {
            // 1. Récupérer les réservations avec les infos client
            const { data: bookingsData, error: fetchError } = await supabase
                .from('bookings')
                .select(`
                    *,
                    profiles:client_id (
                        display_name,
                        avatar_url
                    )
                `)
                .eq('escort_id', user.id)
                .order('booking_date', { ascending: false });

            if (fetchError) {
                throw fetchError;
            }

            // 2. Pour chaque réservation, trouver l'ID de conversation correspondant
            const enrichedBookings = await Promise.all(
                (bookingsData as BookingWithClient[]).map(async (booking) => {
                    if (!booking.client_id) {
                        return { ...booking, conversationId: null };
                    }
                    try {
                        const { data: convData, error: convError } = await supabase
                            .from('conversations')
                            .select('id')
                            .or(`and(participant1_id.eq.${user.id},participant2_id.eq.${booking.client_id}),and(participant1_id.eq.${booking.client_id},participant2_id.eq.${user.id})`)
                            .limit(1)
                            .maybeSingle(); // Prend la première conversation trouvée ou null

                        if (convError) {
                            console.warn(`Could not fetch conversation ID for client ${booking.client_id}:`, convError.message);
                            return { ...booking, conversationId: null };
                        }
                        return { ...booking, conversationId: convData?.id ?? null };
                    } catch (convCatchError) {
                         console.warn(`Error fetching conversation ID for client ${booking.client_id}:`, convCatchError);
                         return { ...booking, conversationId: null };
                    }
                })
            );

            setBookings(enrichedBookings);

        } catch (err) {
            console.error("Error loading booking history:", err);
            const errorMessage = (err instanceof Error && err.message) ? err.message : t('errors.fetchError', { ns: 'escort-bookings-history' });
            setError(errorMessage);
            toast({ 
              title: t('common:error'), 
              description: errorMessage, 
              variant: 'destructive' 
            });
            setBookings([]); // Vider en cas d'erreur
        } finally {
            setIsLoading(false);
        }
    }, [supabase, user, t, toast]);

    useEffect(() => {
        loadBookingHistory();
    }, [loadBookingHistory]);

    const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
        switch (status?.toLowerCase()) {
            case 'confirmed':
                return 'default'; // Vert ou couleur par défaut positive
             case 'completed':
                 return 'secondary'; // Gris ou couleur neutre
            case 'pending':
                return 'outline'; // Jaune ou contour
            case 'cancelled':
            case 'refused':
                return 'destructive'; // Rouge
            default:
                return 'secondary';
        }
    };

    const formatBookingDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }) +
               ' ' + date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
    };

    if (isLoading) {
        return <div className="p-4 md:p-6">{t('common:loading')}...</div>;
    }

    if (error) {
        return (
            <Alert variant="destructive" className="m-4 md:m-6">
                <AlertTitle>{t('common:error')}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-4">
            <h2 className="text-2xl font-semibold">{t('title', { ns: 'escort-bookings-history' })}</h2>

            {bookings.length === 0 ? (
                <Alert>
                     <Info className="h-4 w-4" />
                    <AlertTitle>{t('noBookings.title', { ns: 'escort-bookings-history' })}</AlertTitle>
                    <AlertDescription>
                        {t('noBookings.description', { ns: 'escort-bookings-history' })}
                    </AlertDescription>
                </Alert>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('table.header.client', { ns: 'escort-bookings-history' })}</TableHead>
                            <TableHead>{t('table.header.date', { ns: 'escort-bookings-history' })}</TableHead>
                            <TableHead>{t('table.header.duration', { ns: 'escort-bookings-history' })}</TableHead>
                            <TableHead className="text-right">{t('table.header.price', { ns: 'escort-bookings-history' })}</TableHead>
                            <TableHead>{t('table.header.status', { ns: 'escort-bookings-history' })}</TableHead>
                            <TableHead>{t('table.header.actions', { ns: 'common' })}</TableHead> {/* Colonne Actions */}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {bookings.map((booking) => (
                            <TableRow key={booking.id}>
                                <TableCell>
                                     {/* Client non cliquable pour l'instant, on ajoute juste l'action */}
                                    <div className="flex items-center space-x-2">
                                        <Avatar className="h-8 w-8">
                                            <AvatarImage src={booking.profiles?.avatar_url ?? undefined} alt={booking.profiles?.display_name ?? 'Client'} />
                                            <AvatarFallback>{booking.profiles?.display_name?.charAt(0)?.toUpperCase() ?? 'C'}</AvatarFallback>
                                        </Avatar>
                                        <span>{booking.profiles?.display_name ?? t('unknownClient', { ns: 'escort-bookings-history' })}</span>
                                    </div>
                                </TableCell>
                                <TableCell>{formatBookingDateTime(booking.booking_date)}</TableCell>
                                <TableCell>{booking.duration ?? 'N/A'}</TableCell>
                                <TableCell className="text-right">
                                    {formatCurrency(booking.price, booking.currency ?? 'EUR')}
                                </TableCell>
                                <TableCell>
                                    <Badge variant={getStatusVariant(booking.status)}>
                                        {t(`common:bookingStatus.${booking.status}`, booking.status)} {/* Utiliser i18n pour traduire le statut */}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {booking.conversationId ? (
                                        <Link
                                            to={`/${i18n.language}/messages/${booking.conversationId}`}
                                            className="text-pink-600 hover:text-pink-800 inline-flex items-center"
                                            title={t('actions.goToConversation', { ns: 'escort-bookings-history' })}
                                        >
                                            <MessageCircle className="h-5 w-5" />
                                        </Link>
                                    ) : (
                                         <span title={t('actions.noConversation', { ns: 'escort-bookings-history' })}
                                               className="text-gray-400 inline-flex items-center cursor-not-allowed">
                                              <MessageCircle className="h-5 w-5" />
                                         </span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};

export default EscortBookingsHistory; 